// How hard the engine thinks when the player asks for an evaluation. Mirrors the C# EvaluationStrength
// enum (the API binds the names). Only labels live here; depths are the server's
// (Code/EvaluationStrength.cs).
export type EvaluationStrength = 'Fast' | 'Standard' | 'Deep' | 'Maximum';

export const DEFAULT_STRENGTH: EvaluationStrength = 'Standard';

export type StrengthOption = {
  key: EvaluationStrength;
  label: string;
  // The trade-off in words. Times are worst case (an ~80-move game, cold cache); re-measure if the
  // server depths change.
  hint: string;
};

export const STRENGTH_OPTIONS: StrengthOption[] = [
  { key: 'Fast', label: 'Fast', hint: 'A quick pass. About 2 seconds' },
  { key: 'Standard', label: 'Standard', hint: 'The usual depth. About 4 seconds' },
  { key: 'Deep', label: 'Deep', hint: 'Sees further into tactics. About 8 seconds' },
  { key: 'Maximum', label: 'Maximum', hint: 'Strongest available. About 40 seconds' },
];

// Kept in localStorage: a finished evaluation remounts the button, so component state would snap back
// to Standard and the next click would run something other than the label. Wrapped because
// localStorage throws in Safari private mode and when storage is blocked.
export const STRENGTH_KEY = 'syc.evaluationStrength';

function isStrength(value: unknown): value is EvaluationStrength {
  return STRENGTH_OPTIONS.some((o) => o.key === value);
}

export function readStrength(): EvaluationStrength {
  try {
    const raw = localStorage.getItem(STRENGTH_KEY);
    return isStrength(raw) ? raw : DEFAULT_STRENGTH;
  } catch {
    return DEFAULT_STRENGTH;
  }
}

export function writeStrength(strength: EvaluationStrength): void {
  try {
    localStorage.setItem(STRENGTH_KEY, strength);
  } catch {
    // Storage unavailable — the choice just won't survive the reload.
  }
}
